import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';

interface BingoItem {
    id: string;
    title: string;
    description: string;
    category: string;
    difficulty: string;
}

export default function CreateGame() {
    const navigate = useNavigate();
    const api = useApi();
    const { tenant } = useTenant();
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState<any[]>([]);
    const [items, setItems] = useState<BingoItem[]>([]);
    const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
    const [itemSearch, setItemSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        rewardBanner: '',
        departmentId: '',
        startDate: '',
        endDate: '',
        revealEnabled: true,
        revealDay: 'Friday',
        revealTime: '15:00',
        revealDuration: 24,
    });

    const MIN_ITEMS = 25;

    useEffect(() => {
        loadDepartments();
        loadItems();
    }, []);

    async function loadDepartments() {
        try {
            const result = await api.getDepartments();
            setDepartments(result.departments || []);
        } catch (error) {
            console.error('Failed to load departments:', error);
        }
    }

    async function loadItems() {
        try {
            const result = await api.getItems();
            setItems(result.items || []);
        } catch (error) {
            console.error('Failed to load items:', error);
        }
    }

    // Get unique categories from items
    const categories = useMemo(() => {
        const cats = new Set(items.map(item => item.category));
        return Array.from(cats).sort();
    }, [items]);

    // Filter items by search and category
    const filteredItems = useMemo(() => {
        return items.filter(item => {
            const matchesSearch = !itemSearch ||
                item.title.toLowerCase().includes(itemSearch.toLowerCase()) ||
                item.description.toLowerCase().includes(itemSearch.toLowerCase());
            const matchesCategory = !categoryFilter || item.category === categoryFilter;
            return matchesSearch && matchesCategory;
        });
    }, [items, itemSearch, categoryFilter]);

    function toggleItem(itemId: string) {
        setSelectedItemIds(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    }

    function selectAll() {
        setSelectedItemIds(new Set(filteredItems.map(item => item.id)));
    }

    function deselectAll() {
        setSelectedItemIds(new Set());
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        // Validate minimum items
        if (selectedItemIds.size < MIN_ITEMS) {
            alert(`Please select at least ${MIN_ITEMS} items for the bingo game.`);
            return;
        }

        setLoading(true);

        try {
            const payload = {
                name: formData.name,
                description: formData.description,
                rewardBanner: formData.rewardBanner || undefined,
                departmentId: formData.departmentId,
                startDate: formData.startDate,
                endDate: formData.endDate,
                revealSchedule: {
                    enabled: formData.revealEnabled,
                    day: formData.revealDay,
                    time: formData.revealTime,
                    durationHours: Number(formData.revealDuration),
                },
                selectedItemIds: Array.from(selectedItemIds),
            };

            await api.createGame(payload);
            alert('Game created successfully!');
            navigate('/admin/games');
        } catch (error: any) {
            console.error('Failed to create game:', error);
            alert(error.message || 'Failed to create game');
        } finally {
            setLoading(false);
        }
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    }

    function getDifficultyBadge(difficulty: string) {
        const colors: Record<string, string> = {
            easy: 'bg-green-100 text-green-800',
            medium: 'bg-yellow-100 text-yellow-800',
            hard: 'bg-red-100 text-red-800',
        };
        return colors[difficulty.toLowerCase()] || 'bg-gray-100 text-gray-800';
    }

    return (
        <div className="max-w-4xl">
            <h1 className="text-3xl font-bold mb-6 text-text-primary">Create New Game</h1>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info Section */}
                <div className="bg-surface rounded-lg shadow p-6 border border-border space-y-4">
                    <h2 className="text-lg font-semibold text-text-primary">Basic Information</h2>

                    <div>
                        <label htmlFor="name" className="block text-sm font-medium text-text-secondary mb-1">
                            Game Name *
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary"
                            placeholder="e.g., Q4 Engineering Bingo"
                        />
                    </div>

                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-text-secondary mb-1">
                            Description
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            rows={2}
                            value={formData.description}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary"
                            placeholder="Describe the game..."
                        />
                    </div>

                    <div>
                        <label htmlFor="departmentId" className="block text-sm font-medium text-text-secondary mb-1">
                            Department *
                        </label>
                        <select
                            id="departmentId"
                            name="departmentId"
                            required
                            value={formData.departmentId}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary"
                        >
                            <option value="">Select a department</option>
                            {departments.map((dept) => (
                                <option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="rewardBanner" className="block text-sm font-medium text-text-secondary mb-1">
                            🏆 Reward Banner (Optional)
                        </label>
                        <textarea
                            id="rewardBanner"
                            name="rewardBanner"
                            rows={2}
                            value={formData.rewardBanner}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-text-primary"
                            placeholder="e.g., Winner gets a $50 Amazon gift card!"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="startDate" className="block text-sm font-medium text-text-secondary mb-1">
                                Start Date *
                            </label>
                            <input
                                type="date"
                                id="startDate"
                                name="startDate"
                                required
                                value={formData.startDate}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary"
                            />
                        </div>

                        <div>
                            <label htmlFor="endDate" className="block text-sm font-medium text-text-secondary mb-1">
                                End Date *
                            </label>
                            <input
                                type="date"
                                id="endDate"
                                name="endDate"
                                required
                                value={formData.endDate}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary"
                            />
                        </div>
                    </div>
                </div>

                {/* Reveal Schedule Section */}
                <div className="bg-surface rounded-lg shadow p-6 border border-border">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-text-primary">Reveal Schedule</h2>
                        <span className="text-sm text-text-secondary bg-background px-2 py-1 rounded border border-border">
                            🌍 Timezone: {(tenant as any)?.timezone || 'America/New_York'}
                        </span>
                    </div>

                    {/* Enable Toggle */}
                    <div className="flex items-center gap-3 mb-4 p-3 bg-background rounded-md border border-border">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.revealEnabled}
                                onChange={(e) => setFormData({ ...formData, revealEnabled: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                        <div>
                            <span className="font-medium text-text-primary">Enable Reveal Schedule</span>
                            <p className="text-sm text-text-secondary">
                                {formData.revealEnabled
                                    ? 'Leaderboard is only visible during scheduled reveal times'
                                    : 'Leaderboard is always visible to participants'}
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label htmlFor="revealDay" className="block text-sm font-medium text-text-secondary mb-1">
                                Day of Week
                            </label>
                            <select
                                id="revealDay"
                                name="revealDay"
                                value={formData.revealDay}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary"
                            >
                                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                    <option key={day} value={day}>{day}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="revealTime" className="block text-sm font-medium text-text-secondary mb-1">
                                Time (24h)
                            </label>
                            <input
                                type="time"
                                id="revealTime"
                                name="revealTime"
                                value={formData.revealTime}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary"
                            />
                        </div>
                        <div>
                            <label htmlFor="revealDuration" className="block text-sm font-medium text-text-secondary mb-1">
                                Duration (Hours)
                            </label>
                            <input
                                type="number"
                                id="revealDuration"
                                name="revealDuration"
                                min="1"
                                max="72"
                                value={formData.revealDuration}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary"
                            />
                        </div>
                    </div>
                </div>

                {/* Item Selection Section */}
                <div className="bg-surface rounded-lg shadow p-6 border border-border">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-text-primary">
                            Select Bingo Items *
                        </h2>
                        <span className={`text-sm px-3 py-1 rounded-full font-medium ${selectedItemIds.size >= MIN_ITEMS
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {selectedItemIds.size} / {MIN_ITEMS} minimum selected
                        </span>
                    </div>

                    {/* Filters */}
                    <div className="flex gap-4 mb-4">
                        <div className="flex-1">
                            <input
                                type="text"
                                placeholder="Search items..."
                                value={itemSearch}
                                onChange={(e) => setItemSearch(e.target.value)}
                                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary"
                            />
                        </div>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary"
                        >
                            <option value="">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    {/* Bulk actions */}
                    <div className="flex gap-2 mb-4">
                        <button
                            type="button"
                            onClick={selectAll}
                            className="px-3 py-1 text-sm bg-primary text-white rounded hover:opacity-90"
                        >
                            Select All Visible ({filteredItems.length})
                        </button>
                        <button
                            type="button"
                            onClick={deselectAll}
                            className="px-3 py-1 text-sm bg-background text-text-secondary rounded border border-border hover:bg-border"
                        >
                            Deselect All
                        </button>
                    </div>

                    {/* Items grid */}
                    <div className="max-h-80 overflow-y-auto border border-border rounded-md">
                        {filteredItems.length === 0 ? (
                            <div className="p-4 text-center text-text-muted">
                                No items found. Make sure to add items in Item Management first.
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {filteredItems.map(item => (
                                    <label
                                        key={item.id}
                                        className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-background transition-colors ${selectedItemIds.has(item.id) ? 'bg-primary/5' : ''
                                            }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedItemIds.has(item.id)}
                                            onChange={() => toggleItem(item.id)}
                                            className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-text-primary truncate">
                                                    {item.title}
                                                </span>
                                                <span className={`text-xs px-2 py-0.5 rounded ${getDifficultyBadge(item.difficulty)}`}>
                                                    {item.difficulty}
                                                </span>
                                            </div>
                                            <p className="text-sm text-text-secondary truncate">
                                                {item.description}
                                            </p>
                                            <span className="text-xs text-text-muted">{item.category}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Submit buttons */}
                <div className="flex gap-3">
                    <button
                        type="submit"
                        disabled={loading || selectedItemIds.size < MIN_ITEMS}
                        className="flex-1 bg-primary text-white px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Creating...' : 'Create Game'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/admin/games')}
                        className="flex-1 bg-background text-text-secondary px-4 py-2 rounded-md hover:bg-border border border-border"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}
